import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Img from 'react-image';
import format from 'date-fns/format';
import hash from 'object-hash';
import {
  FlexBox,
  Typography,
  Card,
  Box,
  Button,
  Backdrop,
  DotProgress,
  SelectDropdown,
  color
} from '@kis-technologies/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as clientSelectors } from 'store/clients';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { ImageUploadOverlay } from '../Shared/ImageUploadOverlay';
import { MediaResponse, CIMediaProps, MediaTypes } from '../../types';

export const EventImages: React.FC<CIMediaProps> = (props: CIMediaProps) => {
  const [getTicketingEvents, resetEvents, resetError, setEvent, getMedia, deleteMedia, resetAction] = useActions([
    ciActions.getTicketingEvents,
    ciActions.resetEvents,
    ciActions.resetError,
    mediaActions.setEvent,
    mediaActions.getMedia,
    mediaActions.deleteMedia,
    mediaActions.resetAction
  ]);
  const events = useSelector(ciSelectors.selectGroupedEvents);
  const selectPending = useSelector(ciSelectors.selectPending);
  const error = useSelector(ciSelectors.selectError);
  const { pending, successful } = useSelector(mediaSelectors.selectAction);
  const selectedEvent = useSelector(mediaSelectors.selectEvent);
  const eventImages = useSelector(mediaSelectors.selectEventImages);
  const clientId = useSelector(clientSelectors.selectClientId);
  const eventNames = Object.keys(events);
  const hasEvents = eventNames.length > 0;
  const [showImageUploadOverlay, setShowImageUploadOverlay] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  React.useEffect(() => {
    resetError();
    resetEvents();
    setPreviewUrl('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.environment) {
      getTicketingEvents(props.environment.id);
      getMedia({ type: MediaTypes.EVENT, clientIntegrationId: props.environment.id });
      setPreviewUrl('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.environment]);

  React.useEffect(() => {
    if (selectedEvent && eventImages && eventImages.length) {
      const baseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/event_images/`;
      const imgObj = eventImages.find((item: MediaResponse) => {
        return item.name === hash(selectedEvent.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.s3Name) {
        setPreviewUrl(baseUrl.concat(imgObj.s3Name));
      } else {
        setPreviewUrl('');
      }
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, eventImages]);

  React.useEffect(() => {
    if (props.environment && successful) {
      getMedia({ type: MediaTypes.EVENT, clientIntegrationId: props.environment.id });
      setPreviewUrl('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  const onDeleteImage = () => {
    if (eventImages && eventImages.length) {
      const imgObj = eventImages.find((item: MediaResponse) => {
        return item.name === hash(selectedEvent.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.id) {
        deleteMedia(imgObj.id);
      }
    }
    setShowConfirmation(false);
  };

  const EventList = () => (
    <FlexBox direction="column" fullWidth sx={{ minWidth: '100%' }}>
      {events[selectedEvent] &&
        events[selectedEvent].map((event) => (
          <FlexBox key={event.id} direction="row" justify="between" fullWidth margin="1 0">
            <Typography fontSize={16}>{event.name}</Typography>
            <Typography fontSize={16}>
              {event.date ? format(new Date(event.date), 'MMMM dd, yyyy hh:mm a') : ''}
            </Typography>
          </FlexBox>
        ))}
    </FlexBox>
  );

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography fontSize={40} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography fontSize={24} align="center">
                Are you sure you want to delete this image?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          onConfirm={onDeleteImage}
          cancelText="Cancel"
          onCancel={() => setShowConfirmation(false)}
          height={300}
        />
      )}
      {(selectPending || pending) && (
        <Backdrop open={selectPending || pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      {/* <FlexBox direction="column" flexOverride="1" fullWidth> */}
      {hasEvents && (
        <FlexBox direction="column" justify="start" noWrap>
          <FlexBox align="center" margin="0 0 6" direction="row">
            <Typography fontSize={24} fontWeight={700} sx={{ marginRight: '20px' }}>
              <Box width={180} sx={{ whiteSpace: 'nowrap' }}>
                Select an Event
              </Box>
            </Typography>
            <SelectDropdown
              items={eventNames.sort()}
              itemToString={(item: string) => item}
              selectedItem={selectedEvent}
              onChange={setEvent}
              placeholder="Select an event"
            />
          </FlexBox>
          <FlexBox fullWidth direction="row">
            <Box width={650}>
              <Card raised hasBorderRadius>
                <Card.Title
                  gutter={5}
                  title={
                    <FlexBox direction="row" align="center" justify="between" fullWidth>
                      <Typography fontSize={18} fontWeight={600}>
                        List of Events
                      </Typography>
                    </FlexBox>
                  }
                />
                {/* <CardContent gutter={5}> */}
                <FlexBox fullWidth padding="4 0 4 5">
                  <FlexBox fullWidth padding="0 5 0 0" sx={{ overflowY: 'auto', maxHeight: '385px', minWidth: '100%' }}>
                    {!selectedEvent ? (
                      <Typography fontSize={16}>Select an event first then add an image</Typography>
                    ) : (
                      <EventList />
                    )}
                  </FlexBox>
                </FlexBox>
                {/* </CardContent> */}
              </Card>
            </Box>
            <Box margin="0 3" width={325}>
              <Card raised hasBorderRadius>
                <Card.Title
                  title={
                    <Typography fontSize={18} fontWeight={600}>
                      Image Preview
                    </Typography>
                  }
                  gutter={5}
                />
                <Card.Content>
                  <FlexBox padding="2" align="center" justify="center">
                    <FlexBox width={200} height={150} align="center" justify="center">
                      {pending ? (
                        <Typography>Fetching image...</Typography>
                      ) : (
                        <Img
                          src={previewUrl}
                          width="100%"
                          height="100%"
                          style={{ objectFit: 'cover' }}
                          unloader={<img alt="preview" src={NoImagePreview} width={180} height={158} />}
                        />
                      )}
                    </FlexBox>
                    <Box height={20} fullWidth />
                    {selectedEvent && (
                      <>
                        <Button
                          customSize="small"
                          width={250}
                          appearance="primary"
                          isLoading={pending}
                          onClick={() => setShowImageUploadOverlay(true)}
                          data-testid="add-image-button"
                        >
                          {previewUrl ? 'Select Different Image' : 'Add Image'}
                        </Button>
                        {previewUrl ? (
                          <>
                            <Box fullWidth margin="2 0" />
                            <Button
                              customSize="small"
                              width={250}
                              appearance="white"
                              onClick={() => setShowConfirmation(true)}
                              isLoading={pending}
                              data-testid="delete-video-button"
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                      </>
                    )}
                  </FlexBox>
                </Card.Content>
              </Card>
            </Box>
          </FlexBox>
        </FlexBox>
      )}
      {/* ERROR MESSAGE */}
      {error && (
        <Box sx={{ marginLeft: '155px', marginTop: '-20px' }}>
          <Typography color={color.RED} fontSize={16}>
            {error}
          </Typography>
        </Box>
      )}
      {/* </FlexBox> */}
      {showImageUploadOverlay && (
        <ImageUploadOverlay
          imageType="event"
          imageName={selectedEvent}
          clientIntegrationId={props.environment && props.environment.id}
          onCancel={() => setShowImageUploadOverlay(false)}
          onSuccess={() => setShowImageUploadOverlay(false)}
        />
      )}
    </>
  );
};
